import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from '@/shared/ui/components/form/inputs';
import { Text } from '@/shared/ui';
import {
  formatStoredNumber,
  parseMaskedNumber,
  formatStoredInteger,
  parseMaskedInteger,
  formatStoredPercentage,
  parseMaskedPercentage,
  sanitizePercentageInput,
} from '@/shared/utils';
import { useAgencyRequired } from './agency-required-context';

export interface MaskedNumberFieldProps {
  label: string;
  suffix?: string;
  error?: string;
  value: number | null | undefined;
  onChange: (next: number | undefined) => void;
  /** Caps how many digits the user can type (e.g. 4 for year of construction). */
  maxDigits?: number;
  /**
   * The form field key. Marks the field required when the agency requires
   * it, and tags it so a blocked save can scroll to it.
   */
  field?: string;
}

/**
 * Pins a unit suffix inside the control area (below the label) with input padding.
 *
 * @param props - Optional suffix and the labeled TextInput.
 * @returns Wrapped field with visible trailing unit.
 */
function withInputSuffix(suffix: string | undefined, input: ReactNode): ReactNode {
  if (!suffix) return input;
  return (
    <div className="relative [&_input]:pr-(--space-14)">
      {input}
      <Text
        component="span"
        aria-hidden="true"
        className="pointer-events-none absolute bottom-3 right-3 z-10 text-sm text-[var(--text-tertiary)]"
        style={{ fontFamily: 'var(--font-family)' }}
      >
        {suffix}
      </Text>
    </div>
  );
}

/**
 * Currency/metric input that displays its stored numeric value using the active
 * locale's grouping and decimal separators (e.g. `550.000,00` in pt-BR) while
 * keeping the underlying form value a plain number. Keystrokes are parsed as cents.
 *
 * @param {MaskedNumberFieldProps} props - The field label, suffix, error, value and change handler.
 * @returns {JSX.Element} The rendered masked numeric field.
 */
export function MaskedNumberField({ label, suffix, error, value, onChange, maxDigits, field }: MaskedNumberFieldProps) {
  const { i18n } = useTranslation();
  const display = formatStoredNumber(value, i18n.language);
  const required = useAgencyRequired(field ?? '');

  return (
    <>
      {withInputSuffix(
        suffix,
        <TextInput
          label={label}
          required={required}
          data-field={field}
          error={error}
          inputMode="numeric"
          value={display}
          placeholder={formatStoredNumber(0, i18n.language)}
          onChange={e => onChange(parseMaskedNumber(e.target.value, maxDigits))}
        />,
      )}
    </>
  );
}

/**
 * Integer input without thousands grouping (e.g. year `2007`, not `2.007`).
 * No decimal places. Keystrokes are parsed as whole numbers.
 *
 * @param {MaskedNumberFieldProps} props - The field label, suffix, error, value and change handler.
 * @returns {JSX.Element} The rendered masked integer field.
 */
export function MaskedIntegerField({ label, suffix, error, value, onChange, maxDigits }: MaskedNumberFieldProps) {
  const { i18n } = useTranslation();
  const display = formatStoredInteger(value, i18n.language);

  return (
    <>
      {withInputSuffix(
        suffix,
        <TextInput
          label={label}
          error={error}
          inputMode="numeric"
          value={display}
          placeholder="0"
          onChange={e => onChange(parseMaskedInteger(e.target.value, maxDigits))}
        />,
      )}
    </>
  );
}

/**
 * Percentage input that accepts a locale decimal separator (`,` in pt-BR) while
 * storing a plain number. Local text state preserves a trailing comma while typing.
 *
 * @param {MaskedNumberFieldProps} props - The field label, suffix, error, value and change handler.
 * @returns {JSX.Element} The rendered masked percentage field.
 */
export function MaskedPercentageField({ label, suffix, error, value, onChange }: MaskedNumberFieldProps) {
  const { i18n } = useTranslation();
  const focusedRef = useRef(false);
  const [text, setText] = useState(() => formatStoredPercentage(value, i18n.language));

  useEffect(() => {
    if (!focusedRef.current) setText(formatStoredPercentage(value, i18n.language));
  }, [value, i18n.language]);

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const next = sanitizePercentageInput(e.target.value);
    setText(next);
    onChange(parseMaskedPercentage(next));
  }

  function handleBlur() {
    focusedRef.current = false;
    setText(formatStoredPercentage(parseMaskedPercentage(text), i18n.language));
  }

  return (
    <>
      {withInputSuffix(
        suffix,
        <TextInput
          label={label}
          error={error}
          inputMode="decimal"
          value={text}
          placeholder="0"
          onFocus={() => { focusedRef.current = true; }}
          onBlur={handleBlur}
          onChange={handleChange}
        />,
      )}
    </>
  );
}
